/**
 * AttorneysIndexPage — /attorneys/
 * Реестр юристов фирмы. Устройство повторяет morganlegalgroup.com/attorneys/:
 * тёмный герой, сетка карточек 1/2/3 с портретом 4:5, монограмма из инициалов
 * там, где фотографии ещё нет, и полная разметка Person на каждого.
 * Оформление — здешнее (navy / gold / cream), а не перенесённое.
 *
 * Источник — единственный: `attorneyOrder` + `attorneys` из data/attorneys.ts.
 * Второго списка здесь нет намеренно: перечень, набранный руками, разошёлся бы
 * с реестром при первом же добавлении юриста и промолчал бы об этом.
 *
 * У Расселла Моргана две страницы (/russel-morgan-esq/ — профиль,
 * /russel-morgan-estate-planning-lawyer-in-nyc/ — посадочная под запрос).
 * В реестре он один: дубль в списке людей читается как два однофамильца.
 *
 * 🔴 Эта страница — главная точка входа к личным страницам юристов: список
 * имён убран из меню вслед за morganlegalgroup. Ломая её, ломаешь связность
 * пятнадцати страниц сразу.
 */
import { useEffect } from 'react';
import { ArrowRight, Phone, Calendar, GraduationCap, Scale, Globe } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import { attorneys, attorneyOrder, type Attorney } from '@/data/attorneys';
import { applyPageMeta } from '@/lib/pageMeta';

const SITE = 'https://www.morganlegalny.com';
const metaTitle = 'Our Attorneys — Estate & Elder Law | Morgan Legal Group';
const metaDescription =
  'Meet the attorneys of Morgan Legal Group, P.C. — estate planning, probate, elder law, guardianship, real estate and litigation, in New York and Florida.';

/** Посадочные страницы под запрос — не отдельные люди, в реестре не показываются. */
const DUPLICATE_PROFILES = new Set(['russel-morgan-estate-planning-lawyer-in-nyc']);

/** Порядок групп задан старшинством роли, а не алфавитом. */
const GROUPS: { label: string; match: (a: Attorney) => boolean }[] = [
  { label: 'Founding Partner', match: (a) => /founder|founding/i.test(a.title) },
  { label: 'Of Counsel', match: (a) => /of counsel/i.test(a.title) },
  { label: 'Executive Advisory', match: (a) => /executive advisor/i.test(a.title) },
  { label: 'Senior Associates', match: (a) => /senior/i.test(a.title) },
  { label: 'Associates', match: () => true },
];

const stripSuffix = (n: string) => n.replace(/,?\s*Esq\.?\s*$/i, '').trim();
const hasEsq = (n: string) => /,?\s*Esq\.?\s*$/i.test(n);
const initials = (n: string) =>
  stripSuffix(n)
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('');
const isPlaceholder = (url: string) => url.includes('placeholder');

/**
 * Узел Person собирается из тех полей, которые у юриста ЕСТЬ. Пустое поле не
 * выводится: заявка без содержания хуже отсутствия заявки.
 */
function personNode(a: Attorney) {
  const url = `${SITE}${a.url}`;
  const node: Record<string, unknown> = {
    '@type': ['Person', 'Attorney'],
    '@id': `${url}#person`,
    name: stripSuffix(a.name),
    jobTitle: a.title,
    url,
    description: a.heroLead,
    worksFor: { '@id': `${SITE}/#organization` },
  };
  if (hasEsq(a.name)) node.honorificSuffix = 'Esq.';
  if (!isPlaceholder(a.photos[0].fallback)) node.image = `${SITE}${a.photos[0].fallback}`;
  if (a.practiceAreas.length) node.knowsAbout = a.practiceAreas.map((s) => s.replace(/-/g, ' '));
  if (a.sameAs?.length) node.sameAs = a.sameAs;

  const edu = a.credentials.filter((c) => c.type === 'education');
  if (edu.length) {
    node.alumniOf = edu.map((c) => ({ '@type': 'EducationalOrganization', name: c.value }));
  }
  const adm = a.credentials.filter((c) => c.type === 'admission');
  if (adm.length) {
    node.hasCredential = adm.map((c) => ({
      '@type': 'EducationalOccupationalCredential',
      credentialCategory: 'license',
      name: c.value,
    }));
  }
  const lang = a.credentials.filter((c) => c.type === 'language');
  if (lang.length) node.knowsLanguage = lang.map((c) => c.value);
  return node;
}

export default function AttorneysIndexPage() {
  const canonical = `${SITE}/attorneys/`;

  const roster: Attorney[] = attorneyOrder
    .filter((slug) => !DUPLICATE_PROFILES.has(slug))
    .map((slug) => attorneys[slug])
    .filter(Boolean);

  // Каждый юрист попадает ровно в одну группу: перебор идёт по порядку,
  // последняя группа ловит всех оставшихся.
  const grouped = GROUPS.map(({ label }) => ({ label, items: [] as Attorney[] }));
  for (const a of roster) {
    const i = GROUPS.findIndex((g) => g.match(a));
    grouped[i === -1 ? GROUPS.length - 1 : i].items.push(a);
  }

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${canonical}#page`,
      url: canonical,
      name: metaTitle,
      description: metaDescription,
      isPartOf: { '@id': `${SITE}/#website` },
      about: { '@id': `${SITE}/#organization` },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Our Attorneys', item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${canonical}#attorneys-list`,
      name: 'Morgan Legal Group Attorneys',
      itemListElement: roster.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: personNode(a),
      })),
    },
  ];

  useEffect(() => {
    if (typeof document !== 'undefined') {
      applyPageMeta({ title: metaTitle, description: metaDescription, url: canonical });
    }
  }, [canonical]);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={schema} />
      <Navbar />
      <main>
        <section className="relative bg-navy pt-28 lg:pt-32 pb-16 lg:pb-20 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
            <span
              className="absolute bottom-0 -right-8 font-display text-[10rem] lg:text-[16rem] font-bold text-white leading-none whitespace-nowrap"
              style={{ opacity: 0.03 }}
            >
              ATTORNEYS
            </span>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
              <a href="/" className="hover:text-gold transition-colors">
                Home
              </a>
              <span className="mx-2 text-gold/60">/</span>
              <span className="text-white/80">Our Attorneys</span>
            </nav>
            <div className="max-w-3xl">
              <div className="section-label mb-5 text-gold">
                <span className="inline-block w-10 h-0.5 bg-gold align-middle mr-2" />
                Our Team
              </div>
              <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
                Meet Our Attorneys
              </h1>
              <p className="font-body text-white/85 text-lg leading-relaxed mb-8">
                {roster.length} attorneys practice at Morgan Legal Group, P.C. — across estate planning, probate and
                trust administration, elder law and Medicaid planning, guardianship, real estate, family law and
                litigation. Every matter is handled by the attorney whose practice it belongs to.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="/appointment/" className="btn-gold">
                  <Calendar className="w-4 h-4" />
                  Schedule Your Consultation
                </a>
                <a href="tel:8885291315" className="btn-ghost-gold">
                  <Phone className="w-4 h-4" />
                  888-529-1315
                </a>
              </div>
            </div>
          </div>
        </section>

        {grouped.map(({ label, items }) =>
          items.length === 0 ? null : (
            <section key={label} className="py-14 lg:py-16 odd:bg-white even:bg-cream">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="section-label mb-6">{label}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {items.map((a) => {
                    const edu = a.credentials.find((c) => c.type === 'education');
                    const lang = a.credentials.filter((c) => c.type === 'language');
                    const photo = a.photos[0];
                    return (
                      <a
                        key={a.slug}
                        href={a.url}
                        className="group flex flex-col bg-white border border-navy/10 hover:border-gold hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                      >
                        <div className="relative overflow-hidden bg-cream" style={{ aspectRatio: '4 / 5' }}>
                          {isPlaceholder(photo.fallback) ? (
                            <div className="w-full h-full flex flex-col items-center justify-center">
                              <span
                                aria-hidden="true"
                                className="w-20 h-20 bg-gold text-white font-display text-2xl font-bold flex items-center justify-center"
                              >
                                {initials(a.name)}
                              </span>
                              <span className="mt-4 font-body text-[0.65rem] tracking-[0.15em] uppercase text-navy/50">
                                Photo Coming Soon
                              </span>
                            </div>
                          ) : (
                            <picture>
                              <source srcSet={photo.webp} type="image/webp" />
                              <img
                                src={photo.fallback}
                                alt={photo.alt}
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                              />
                            </picture>
                          )}
                        </div>

                        <div className="flex flex-col flex-1 p-6">
                          <h2 className="font-display text-navy text-xl font-bold leading-tight">{a.name}</h2>
                          <p className="font-body text-gold text-sm mt-1 mb-4">{a.title}</p>

                          {a.titleSubline && (
                            <p className="font-body text-navy/60 text-sm mb-3 flex items-start gap-1.5">
                              <Scale className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" aria-hidden="true" />
                              <span>{a.titleSubline}</span>
                            </p>
                          )}
                          {edu && (
                            <p className="font-body text-navy/60 text-sm mb-3 flex items-start gap-1.5">
                              <GraduationCap className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" aria-hidden="true" />
                              <span>{edu.value}</span>
                            </p>
                          )}
                          {lang.length > 0 && (
                            <p className="font-body text-navy/60 text-sm mb-3 flex items-start gap-1.5">
                              <Globe className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" aria-hidden="true" />
                              <span>Also practices in {lang.map((c) => c.value).join(', ')}</span>
                            </p>
                          )}

                          <p className="font-body text-navy/70 text-sm leading-relaxed mb-5 line-clamp-3 flex-1">
                            {a.heroLead}
                          </p>
                          <span className="inline-flex items-center gap-1.5 text-gold font-body text-sm font-semibold mt-auto">
                            Read full bio
                            <ArrowRight
                              className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                              aria-hidden="true"
                            />
                          </span>
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            </section>
          ),
        )}
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
